import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Check, ChevronRight} from "lucide-react";
import React from "react";
import {useTranslation} from "react-i18next";
import {
  curriculumSessionMessageKey,
  curriculumSessionState,
  partnersOf,
  sessionCardStates,
} from "business/curriculumSession";
import {convertUrl, initials} from "common/helpers";
import {color, font, radius, type} from "common/tokens";
import {AvatarGroup} from "./Avatar";
import Card from "./Card";
import Chip from "./Chip";
import CtaButton from "./CtaButton";
import ProgressRing from "./ProgressRing";
import ShimmerImage from "./ShimmerImage";

const RING_SIZE = 33;

const useStyles = makeStyles({
  banner: {
    width: '100%',
    height: 164,
  },
  bannerCompact: {
    height: 116,
  },
  media: {
    position: 'relative',
  },
  statusBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
  },
  // Compact cards trade the progress ring for a bar under the image, so the
  // body carries only the deadline and the action.
  progressTrack: {
    height: 4,
    background: color.RING_TRACK,
    width: '100%',
  },
  progressFill: {
    height: '100%',
    background: color.RING_FILL,
    transition: 'width 0.2s ease',
  },
  // Stretched to its row so a session with no banner is a full-height card
  // beside the ones that have one, instead of a stub.
  compactCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  bodyCompact: {
    padding: 14,
    gap: 10,
    flex: 1,
  },
  compactSpacer: {
    flex: 1,
  },
  compactFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  body: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    ...type.sessionTitle,
    color: color.CARD_TITLE,
    margin: 0,
    flex: '1 0 0',
    minWidth: 0,
    wordBreak: 'break-word',
  },
  doneCircle: {
    width: RING_SIZE,
    height: RING_SIZE,
    borderRadius: '50%',
    background: color.ACCENT,
    color: color.AVATAR_DONE_TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chevron: {
    background: color.SURFACE_MUTED,
    borderRadius: radius.PILL,
    padding: 6,
    display: 'flex',
    color: color.CARD_TITLE,
    flexShrink: 0,
  },
  message: {
    ...type.chip,
    fontWeight: font.MEDIUM,
    lineHeight: 1.4,
    color: color.CARD_BODY,
    margin: '12px 0 0',
  },
  statusBlock: {
    marginTop: 16,
    borderTop: `1px solid ${color.DIVIDER}`,
    paddingTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
});

const SessionCard = ({sessionStatus, onOpen, now, selfPicture, compact = false}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const state = curriculumSessionState(sessionStatus, now);
  const session = sessionStatus.session || {};
  const title = session.localeTitle || session.title;
  const banner = convertUrl(session.banner);
  const isExpanded = state.card === sessionCardStates.IN_PROGRESS;
  const isDone = state.card === sessionCardStates.COMPLETED;
  const isStarted = state.card !== sessionCardStates.NOT_STARTED;

  const members = [
    {label: t('you'), isDone: state.iAmDone, image: selfPicture},
    ...partnersOf(sessionStatus).map(partner => ({
      label: initials(partner.name || partner.email),
      name: partner.name,
      isDone: ['PARTIAL_COMPLETE', 'COMPLETED'].includes(partner.partnerStatus),
      image: partner.picture,
    })),
  ];

  // The action is only offered while there is a deadline to act on. Without one
  // the card itself is the way in, which it always is regardless.
  const showCta = state.hasDeadline;

  const dueChip = size => (
    <Chip variant={'due'} size={size} overdue={state.isOverdue}>
      {state.isOverdue
        ? t('sessionDaysOverdue', {count: state.daysOverdue})
        : t('sessionDaysLeft', {count: state.daysRemaining})}
    </Chip>
  );

  const cta = (
    <CtaButton
      fullWidth
      variant={state.isOverdue ? 'danger' : 'primary'}
      onClick={onOpen}
      label={t(state.ctaIsRetake ? 'retakeSession' : 'completeNow')}
    />
  );

  // Shown over the banner, or above the title when the session has no image.
  const statusChip = isStarted && (
    <Chip variant={'status'} done={isDone}>
      {isDone ? t('sessionTagCompleted') : t('sessionTagInProgress')}
    </Chip>
  );

  if (compact) {
    return (
      <Card variant={'media'} onClick={onOpen} className={classes.compactCard}>
        {Boolean(banner) && (
          <div className={classes.media}>
            <ShimmerImage src={banner} className={classNames(classes.banner, classes.bannerCompact)}/>
            {statusChip && <span className={classes.statusBadge}>{statusChip}</span>}
          </div>
        )}
        {state.progress > 0 && (
          <div className={classes.progressTrack}>
            <div className={classes.progressFill} style={{width: `${Math.round(state.progress * 100)}%`}}/>
          </div>
        )}
        <div className={classNames(classes.body, classes.bodyCompact)}>
          {!banner && statusChip && <div className={classes.compactFooter}>{statusChip}</div>}
          <div className={classes.titleRow}>
            <h3 className={classes.title}>{title}</h3>
            {/* Without a CTA the chevron is the only cue that the card opens. */}
            {!showCta && (
              <span className={classes.chevron}><ChevronRight size={16} strokeWidth={2}/></span>
            )}
          </div>
          <span className={classes.compactSpacer}/>
          {state.hasDeadline && (
            <div className={classes.compactFooter}>{dueChip('small')}</div>
          )}
          {showCta && cta}
        </div>
      </Card>
    );
  }

  return (
    <Card variant={'media'} onClick={onOpen}>
      {Boolean(banner) && (
        <div className={classes.media}>
          <ShimmerImage src={banner} className={classes.banner}/>
          {statusChip && <span className={classes.statusBadge}>{statusChip}</span>}
        </div>
      )}
      <div className={classes.body}>
        <div className={classes.titleRow}>
          {isDone ? (
            <span className={classes.doneCircle}><Check size={16} strokeWidth={3}/></span>
          ) : (
            <ProgressRing progress={state.progress} size={RING_SIZE}/>
          )}
          <h3 className={classes.title}>{title}</h3>
          {!isExpanded && (
            <span className={classes.chevron}><ChevronRight size={16} strokeWidth={2}/></span>
          )}
        </div>

        {isExpanded && (
          <>
            <p className={classes.message}>
              {t(curriculumSessionMessageKey(state), {count: state.pendingPartnerCount})}
            </p>
            <div className={classes.statusBlock}>
              <div className={classes.statusLine}>
                {state.hasDeadline && dueChip()}
                <AvatarGroup members={members}/>
              </div>
              {showCta && cta}
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default SessionCard;
